import { APIClient } from "../helpers/api_helper";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";

const api = new APIClient();

const RESOURCE = "/client-record-templates";

export type SchemaFieldType =
  | "text"
  | "textarea"
  | "boolean"
  | "select"
  | "section"
  | "consent_text"
  | "consent_checkbox"
  | "signature";

/** Aliases accepted when reading templates; persisted type remains `textarea`. */
export const ANAMNESIS_TEXTAREA_ALIASES = ["long_text"] as const;

/** Generous fill height for protocol / long clinical text (`ADJ-F2-124`). */
export const ANAMNESIS_TEXTAREA_FILL_ROWS = 12;

/** Builder preview height for texto longo. */
export const ANAMNESIS_TEXTAREA_BUILDER_ROWS = 10;

/** Soft ceiling aligned with `clients.notes` (clinical memory). */
export const ANAMNESIS_TEXTAREA_MAX_CHARS = 8000;

/** Show char counter only near the soft ceiling (~80%) — less fill noise (`ADJ-F2-129`). */
export const ANAMNESIS_TEXTAREA_CHAR_COUNT_THRESHOLD = Math.floor(
  ANAMNESIS_TEXTAREA_MAX_CHARS * 0.8
);

export const normalizeSchemaFieldType = (
  rawType: string | undefined | null
): SchemaFieldType => {
  const type = String(rawType ?? "text").trim();

  if ((ANAMNESIS_TEXTAREA_ALIASES as readonly string[]).includes(type)) {
    return "textarea";
  }

  return (type || "text") as SchemaFieldType;
};

export const isAnamnesisAnswerableFieldType = (type: SchemaFieldType): boolean =>
  !["section", "consent_text", "signature"].includes(type);

export interface SchemaFieldDefinition {
  slug?: string;
  name?: string;
  label: string;
  type: SchemaFieldType;
  required?: boolean;
  /** Legal term body for `consent_text` blocks. */
  body?: string;
  options?: string[] | { value: string; label: string }[];
  /** GAP-F2-120 — clinical alert gate on positive answer. */
  clinical_gate?: boolean;
  clinical_gate_trigger?: boolean | string | Array<boolean | string>;
  clinical_gate_service_ids?: number[];
  clinical_gate_product_ids?: number[];
}

export interface ClientRecordTemplateSchema {
  fields?: SchemaFieldDefinition[];
  [key: string]: unknown;
}

export type ClientRecordTemplateType = "anamnesis" | "evolution";

export interface ClientRecordTemplate {
  id: number;
  tenant_id: number;
  title: string;
  record_type?: ClientRecordTemplateType;
  schema_json: ClientRecordTemplateSchema;
  is_active: boolean;
  source_preset_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateClientRecordTemplatePayload {
  title: string;
  record_type?: ClientRecordTemplateType;
  schema_json: ClientRecordTemplateSchema;
  is_active?: boolean;
}

export type UpdateClientRecordTemplatePayload =
  Partial<CreateClientRecordTemplatePayload>;

export interface ClientRecordTemplateListParams extends PaginationParams {
  is_active?: boolean;
  record_type?: ClientRecordTemplateType;
}

export const getAll = (
  params?: ClientRecordTemplateListParams
): Promise<PaginatedApiResponse<ClientRecordTemplate>> =>
  api.get<PaginatedApiResponse<ClientRecordTemplate>>(RESOURCE, params);

export const getById = (id: number): Promise<ApiResponse<ClientRecordTemplate>> =>
  api.get<ApiResponse<ClientRecordTemplate>>(`${RESOURCE}/${id}`);

export const create = (
  payload: CreateClientRecordTemplatePayload
): Promise<ApiResponse<ClientRecordTemplate>> =>
  api.create<ApiResponse<ClientRecordTemplate>>(RESOURCE, payload);

export const update = (
  id: number,
  payload: UpdateClientRecordTemplatePayload
): Promise<ApiResponse<ClientRecordTemplate>> =>
  api.update<ApiResponse<ClientRecordTemplate>>(`${RESOURCE}/${id}`, payload);

export const deleteTemplate = (
  id: number
): Promise<ApiResponse<Record<string, never>>> =>
  api.delete<ApiResponse<Record<string, never>>>(`${RESOURCE}/${id}`);

export { copyFromPreset, getPresets as getTemplatePresets } from "./clientRecordTemplatePresetService";
export type { ClientRecordTemplatePreset } from "./clientRecordTemplatePresetService";
